import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StanLink, StanProgressBar } from '@/components';
import { RegisterForm } from '@/features/auth/components/RegisterForm';
import { useSessionCountry } from '@/features/auth/hooks/useSessionCountry';
import { useUrlPrefill } from '@/features/auth/hooks/useUrlPrefill';
import { getGDPRComplianceDocument } from '@/features/auth/utils/gdpr';
import { LEGAL_URLS } from '@/lib/constants';

const RegisterPage = () => {
    const [searchParams] = useSearchParams();
    const { country } = useSessionCountry();
    const prefill = useUrlPrefill();

    // Initialize username state with pre-filled value from URL
    const [username, setUsername] = useState(prefill.username ?? '');

    // Get GDPR compliance document URL if user is in EU/UK
    const gdprComplianceDocument = getGDPRComplianceDocument(country);

    // Capture redirectTo from URL params and store in sessionStorage
    useEffect(() => {
        const redirectTo = searchParams.get('redirectTo');
        if (redirectTo) {
            sessionStorage.setItem('redirectTo', redirectTo);
        }
    }, [searchParams]);

    // Dynamic greeting based on username input
    const displayUsername = username || 'Username';

    return (
        <div className='mx-auto w-full max-w-md px-8'>
            {/* Progress Bar */}
            <div className='mx-auto w-1/2 py-7'>
                <StanProgressBar value={25} min={0} max={100} progressColor='#a286ff' barColor='#eef1f5' />
            </div>

            {/* Greeting */}
            <div className='mb-6 text-center'>
                <h1 className='text-heading-h2 text-dark'>Hey @{displayUsername} 👋</h1>
                <p className='text-body-lg text-gray mt-1'>Let&apos;s monetize your following!</p>
            </div>

            {/* Form */}
            <div className='pt-6'>
                <RegisterForm onUsernameChange={setUsername} defaultCountry={country} />
            </div>

            {/* Terms of Service */}
            <div className='text-center'>
                <p className='text-body-sm text-gray'>
                    By continuing, you agree to our <br />
                    <a
                        href={LEGAL_URLS.TERMS_OF_SERVICE}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='font-medium underline focus-visible:ring-2'
                    >
                        Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                        href={LEGAL_URLS.PRIVACY_POLICY}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='font-medium underline focus-visible:ring-2'
                    >
                        Privacy Policy.
                    </a>
                    {gdprComplianceDocument && (
                        <>
                            {' '}
                            Your data is protected under{' '}
                            <a
                                href={gdprComplianceDocument}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='font-medium underline focus-visible:ring-2'
                            >
                                GDPR.
                            </a>
                        </>
                    )}
                </p>
            </div>

            {/* Login Link */}
            <div className='mt-5 text-center'>
                <StanLink to='/login' className='text-body-sm text-gray group'>
                    Have an account?{' '}
                    <span className='text-brand-main cursor-pointer font-medium underline underline-offset-2 group-focus-visible:ring-2'>
                        Login
                    </span>
                </StanLink>
            </div>
        </div>
    );
};

export default RegisterPage;
